import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {FieldDataMigrationType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {NumItemsInTagSchemaQuery} from 'schemas/fields/field-migration/__generated/NumItemsInTagSchemaQuery';
import FieldMigrationConfirmRollbackModal from 'schemas/fields/field-migration/FieldMigrationConfirmRollbackModal';
import {publishFieldMigration} from 'schemas/fields/field-migration/SchemaFieldMigrationDAO';
import {SimpleTable} from 'schemas/fields/field-migration/SimpleTable';
import {NUM_ITEMS_IN_TAG_SCHEMA_QUERY} from 'schemas/fields/field-migration/TagSchemaFieldMigrationCreationModal';
import {TagSchemaFileSettingsQuery_tagSchema_TagSchemaNode_activeMigration_FieldDataMigrationNode as ActiveMigration} from 'settings/registry/__generated/TagSchemaFileSettingsQuery';
import {ButtonStyle} from 'ui-library/buttons/Button';
import TextField from 'ui-library/inputs/TextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import Tag, {TagColor} from 'ui-library/Tag';
import {withLoadingScreen} from 'util/Promises';

interface FieldAttributes {
  displayName: string;
  warehouseName: string;
}

export interface FieldMigrationReviewModalProps {
  onRequestRollback: () => Promise<void>;
  onRequestPublish: () => Promise<void>;
  onRequestClose: () => void;
  schemaId: string;
  migration: ActiveMigration;
  originalFieldAttributes: FieldAttributes;
  destinationFieldAttributes: FieldAttributes;
}

export function getDateStr(date: Date, separator: string): string {
  return [
    `${date.getMonth() + 1}`,
    `${date.getDate()}`,
    `${date.getFullYear()}`,
    `${date.getHours()}`,
    `${date.getMinutes()}`,
    `${date.getSeconds()}`,
  ].join(separator);
}

export interface MigrationSummaryTableProps {
  schemaId: string;
  numStartingFieldValues: number;
  numMigratedFieldValues: number;
}

export function MigrationSummaryTable(props: MigrationSummaryTableProps): JSX.Element | null {
  const {schemaId, numStartingFieldValues, numMigratedFieldValues} = props;
  let numItems: number | null = null;
  const numItemQueryResult = useBenchlingQuery<NumItemsInTagSchemaQuery>(NUM_ITEMS_IN_TAG_SCHEMA_QUERY, {
    variables: {id: schemaId},
  });
  if (numItemQueryResult.type === QueryResultType.SUCCESS) {
    assert(
      !isInaccessible(numItemQueryResult.data.tagSchema),
      `Expected to fetch a TagSchema, but it was inaccessible (tagSchemaId=${schemaId})`
    );
    numItems = numItemQueryResult.data.tagSchema?.numItems;
  }
  return (
    <SimpleTable
      columns={['Items', 'Migrated', 'Cannot migrate']}
      values={getTableValues(numStartingFieldValues, numMigratedFieldValues, numItems)}
    />
  );
}

function getTableValues(
  numStartingFieldValues: number,
  numMigratedFieldValues: number,
  numItems: number | null
): Array<Array<string>> {
  const numNotMigratedFieldValues = numStartingFieldValues - numMigratedFieldValues;
  const percentMigrated = Math.round((numMigratedFieldValues / numStartingFieldValues) * 100);
  const percentNotMigrated = 100 - percentMigrated;
  const percentMigratedStr = numStartingFieldValues !== 0 ? `${percentMigrated}%` : 'N/A';
  const percentNotMigratedStr = numStartingFieldValues !== 0 ? `${percentNotMigrated}%` : 'N/A';

  const migratedStr = `${numMigratedFieldValues} (${percentMigratedStr})`;
  const notMigratedStr = `${numNotMigratedFieldValues} (${percentNotMigratedStr})`;
  const values = [['Values', migratedStr, notMigratedStr]];
  if (numItems && numItems > 0) {
    values.push(['Empty values', `${numItems - numStartingFieldValues} (100%)`, '-']);
  }
  return values;
}

export function FieldMigrationReviewModal(props: FieldMigrationReviewModalProps): JSX.Element {
  const {
    onRequestRollback,
    onRequestPublish,
    onRequestClose,
    migration,
    originalFieldAttributes,
    destinationFieldAttributes,
  } = props;
  const defaultNewSourceFieldDisplayName =
    migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD
      ? `${originalFieldAttributes.displayName} (${getDateStr(new Date(), '-')})`
      : originalFieldAttributes.displayName;
  const defaultNewSourceFieldWarehouseName =
    migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD
      ? `${originalFieldAttributes.warehouseName}_${getDateStr(new Date(), '_')}`
      : originalFieldAttributes.warehouseName;
  const defaultNewDestinationFieldDisplayName =
    migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD
      ? originalFieldAttributes.displayName
      : destinationFieldAttributes.displayName;
  const defaultNewDestinationFieldWarehouseName =
    migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD
      ? originalFieldAttributes.warehouseName
      : destinationFieldAttributes.warehouseName;

  const [newSourceFieldDisplayName, setNewSourceFieldDisplayName] = React.useState(
    defaultNewSourceFieldDisplayName
  );
  const [newSourceFieldWarehouseName, setNewSourceFieldWarehouseName] = React.useState(
    defaultNewSourceFieldWarehouseName
  );
  const [newDestinationFieldDisplayName, setNewDestinationFieldDisplayName] = React.useState(
    defaultNewDestinationFieldDisplayName
  );
  const [newDestinationFieldWarehouseName, setNewDestinationFieldWarehouseName] = React.useState(
    defaultNewDestinationFieldWarehouseName
  );

  let migrationSummaryTable = null;
  if (migration.numStartingFieldValues != null && migration.numMigratedFieldValues != null) {
    migrationSummaryTable = (
      <MigrationSummaryTable
        schemaId={props.schemaId}
        numStartingFieldValues={migration.numStartingFieldValues}
        numMigratedFieldValues={migration.numMigratedFieldValues}
      />
    );
  }

  const openConfirmRollbackModal = (): void => {
    legacyRunModal(FieldMigrationConfirmRollbackModal, {
      onRequestRollback,
      onRequestClose: () => {},
      onRequestCloseParent: onRequestClose,
      sourceField: migration.sourceField!, // TODO (BNCH-86039) sourceField is non-nullable for a migration
    });
  };

  const publishOnClick = async (): Promise<void> => {
    await withLoadingScreen(
      (async () => {
        await publishFieldMigration(
          migration.sourceField!.apiId,
          {
            sourceFieldDisplayName: newSourceFieldDisplayName,
            sourceFieldWarehouseName: newSourceFieldWarehouseName,
            destinationFieldDisplayName: newDestinationFieldDisplayName,
            destinationFieldWarehouseName: newDestinationFieldWarehouseName,
          },
          migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD
        );
        await onRequestPublish();
        onRequestClose();
      })()
    );
  };

  return (
    <Modal
      title="Review & Publish"
      onRequestSubmit={publishOnClick}
      onRequestClose={onRequestClose}
      scrollable={true}
      mediumLarge={true}
      className="modal-text-only"
      buttons={[
        {
          text: 'Rollback migration',
          onClick: openConfirmRollbackModal,
          style: ButtonStyle.NEGATIVE,
          pullLeft: true,
        },
        {text: 'Cancel', cancel: true},
        {text: 'Publish migration', submit: true},
      ]}
    >
      <div className={css(styles.row)}>
        <h4 className={css(styles.fieldTitle)}>Source Field</h4>
        {migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD && (
          <Tag icon="box-archive-arrow-down" color={TagColor.WARN} text="Archiving" />
        )}
      </div>
      <div className={css(styles.row)}>
        <div className={css(styles.rowHalf)}>
          <TextField
            isRequired={true}
            label="Field Name"
            onChange={(e) => {
              setNewSourceFieldDisplayName(e.target.value);
            }}
            autoFocus={false}
            value={newSourceFieldDisplayName}
            disabled={migration.migrationType === FieldDataMigrationType.TO_EXISTING_FIELD}
          />
        </div>
        <div className={css(styles.rowHalf)}>
          <TextField
            isRequired={true}
            label="System name"
            onChange={(e) => {
              setNewSourceFieldWarehouseName(e.target.value);
            }}
            autoFocus={false}
            value={newSourceFieldWarehouseName}
            disabled={migration.migrationType === FieldDataMigrationType.TO_EXISTING_FIELD}
          />
        </div>
      </div>
      <div className={css(styles.row)}>
        <h4 className={css(styles.fieldTitle)}>Destination Field</h4>
        {migration.migrationType === FieldDataMigrationType.TO_NEW_FIELD && (
          <Tag color={TagColor.SUCCESS} text="Creating" />
        )}
      </div>
      <div className={css(styles.row)}>
        <div className={css(styles.rowHalf)}>
          <TextField
            isRequired={true}
            label="Field Name"
            onChange={(e) => {
              setNewDestinationFieldDisplayName(e.target.value);
            }}
            autoFocus={false}
            value={newDestinationFieldDisplayName}
            disabled={migration.migrationType === FieldDataMigrationType.TO_EXISTING_FIELD}
          />
        </div>
        <div className={css(styles.rowHalf)}>
          <TextField
            isRequired={true}
            label="System name"
            onChange={(e) => {
              setNewDestinationFieldWarehouseName(e.target.value);
            }}
            autoFocus={false}
            value={newDestinationFieldWarehouseName}
            disabled={migration.migrationType === FieldDataMigrationType.TO_EXISTING_FIELD}
          />
        </div>
      </div>
      {migrationSummaryTable && <div className={css(styles.tableContainer)}>{migrationSummaryTable}</div>}
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
  },
  rowHalf: {
    width: '50%',
    padding: '4px',
  },
  fieldTitle: {
    marginRight: '8px',
  },
  tableContainer: {
    marginTop: '16px',
  },
});
